import { enGB } from 'date-fns/locale'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useCallback, useEffect, useRef, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FaCheck, FaTimes } from 'react-icons/fa'
import api, { apiUpload } from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

registerLocale('en-GB', enGB)

const PAGE_SIZE = 25
const POLL_MS = 1500
const TERMINAL_RUN = new Set(['complete', 'failed'])

type HumanDecision = 'accept' | 'reject'

interface EvaluationRow {
    id: number
    case_ref: string
    source: string
    geometry_decision: string | null
    geometry_reasons: unknown
    vlm_decision: string | null
    vlm_justification: string | null
    human_decision: string | null
    final_decision: string | null
    created_at: string | null
    signature_data?: string | null
}

interface BackfillRun {
    id: number
    status: string
    filename: string | null
    total: number
    processed: number
    failed: number
}

interface Filters {
    final_decision: string
    layer: string
    source: string
    case_ref: string
    dateFrom: string
    dateTo: string
}

const EMPTY_FILTERS: Filters = {
    final_decision: '',
    layer: '',
    source: '',
    case_ref: '',
    dateFrom: '',
    dateTo: '',
}

const formatDateTime = (value: string | null): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Local yyyy-mm-dd; toISOString would shift the day for anyone east of UTC. */
const toIsoDate = (date: Date): string => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

const fromIsoDate = (value: string): Date | null => {
    if (!value) return null
    const parsed = new Date(`${value}T00:00:00`)
    return isNaN(parsed.getTime()) ? null : parsed
}

const parseReasons = (value: unknown): string[] => {
    if (value == null || value === '') return []
    if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean)
    if (typeof value === 'string') {
        try {
            return parseReasons(JSON.parse(value))
        } catch {
            return [value]
        }
    }
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).map(([key, item]) => `${key}: ${item}`)
    }
    return [String(value)]
}

const imageSrc = (signatureData: string | null | undefined): string | null => {
    if (!signatureData) return null
    if (signatureData.startsWith('data:')) return signatureData
    return `data:image/png;base64,${signatureData}`
}

const errorMessage = (error: unknown, fallback: string): string => {
    const err = error as { response?: { data?: { message?: string } }; message?: string }
    return err?.response?.data?.message || err?.message || fallback
}

export const SignatureReviewView = () => {
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
    const [page, setPage] = useState(1)
    const skipDebounceRef = useRef(true)
    const [rows, setRows] = useState<EvaluationRow[]>([])
    const [total, setTotal] = useState(0)
    const [loaded, setLoaded] = useState(false)
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [detail, setDetail] = useState<EvaluationRow | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [labelling, setLabelling] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [run, setRun] = useState<BackfillRun | null>(null)
    const [runError, setRunError] = useState('')
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pollGenRef = useRef(0)

    const stopPolling = useCallback(() => {
        pollGenRef.current += 1
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current)
            pollTimerRef.current = null
        }
    }, [])

    useEffect(() => () => stopPolling(), [stopPolling])

    const fetchList = useCallback(async (nextFilters: Filters, nextPage: number) => {
        Loading.circle('Loading signature evaluations...')
        try {
            const response = await api.post('signature-review-list', {
                final_decision: nextFilters.final_decision || undefined,
                layer: nextFilters.layer || undefined,
                source: nextFilters.source.trim() || undefined,
                case_ref: nextFilters.case_ref.trim() || undefined,
                dateFrom: nextFilters.dateFrom || undefined,
                dateTo: nextFilters.dateTo || undefined,
                page: nextPage,
                pageSize: PAGE_SIZE,
            })
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not load signature evaluations.', 'Okay')
                return
            }
            setRows(Array.isArray(response.data.rows) ? response.data.rows : [])
            setTotal(Number(response.data.total) || 0)
        } catch (error) {
            Report.failure('Error', errorMessage(error, 'Could not load signature evaluations.'), 'Okay')
        } finally {
            Loading.remove()
            setLoaded(true)
        }
    }, [])

    useEffect(() => {
        if (skipDebounceRef.current) {
            skipDebounceRef.current = false
            void fetchList(filters, page)
            return
        }
        const timer = setTimeout(() => void fetchList(filters, page), 350)
        return () => clearTimeout(timer)
    }, [filters, page, fetchList])

    const patchFilters = (patch: Partial<Filters>) => {
        setFilters((current) => ({ ...current, ...patch }))
        setPage(1)
    }

    const goToPage = (next: number) => {
        skipDebounceRef.current = true
        setPage(next)
    }

    const loadDetail = async (id: number) => {
        setSelectedId(id)
        setDetailLoading(true)
        try {
            const response = await api.post('signature-review-detail', { id })
            if (!response.data?.success || !response.data.evaluation) {
                Report.failure('Error', response.data?.message || 'Could not load that signature.', 'Okay')
                return
            }
            setDetail(response.data.evaluation)
        } catch (error) {
            Report.failure('Error', errorMessage(error, 'Could not load that signature.'), 'Okay')
        } finally {
            setDetailLoading(false)
        }
    }

    const recordLabel = async (humanDecision: HumanDecision) => {
        if (!detail) return
        setLabelling(true)
        try {
            const response = await api.post('signature-review-label', {
                id: detail.id,
                human_decision: humanDecision,
            })
            if (!response.data?.success || !response.data.evaluation) {
                Report.failure('Error', response.data?.message || 'Could not record the label.', 'Okay')
                return
            }
            const updated = response.data.evaluation as EvaluationRow
            setDetail((current) =>
                current ? { ...current, ...updated, signature_data: current.signature_data } : current
            )
            await fetchList(filters, page)
        } catch (error) {
            Report.failure('Error', errorMessage(error, 'Could not record the label.'), 'Okay')
        } finally {
            setLabelling(false)
        }
    }

    const pollRun = useCallback(async (runId: number, gen: number) => {
        try {
            const response = await api.post('signature-backfill-status', { runId })
            if (pollGenRef.current !== gen) return
            if (!response.data?.success || !response.data.run) {
                throw new Error(response.data?.message || 'Could not read backfill progress.')
            }
            const next = response.data.run as BackfillRun
            setRun(next)
            setRunError('')
            if (TERMINAL_RUN.has(next.status)) {
                setUploading(false)
                if (next.status === 'complete') {
                    skipDebounceRef.current = true
                    setFilters({ ...EMPTY_FILTERS, source: 'csvUpload' })
                    setPage(1)
                }
                return
            }
            pollTimerRef.current = setTimeout(() => {
                void pollRun(runId, gen)
            }, POLL_MS)
        } catch (error) {
            if (pollGenRef.current !== gen) return
            const status = (error as { response?: { status?: number } })?.response?.status
            if (status === 404) {
                setUploading(false)
                setRunError('Signature run not found.')
                return
            }
            setRunError(errorMessage(error, 'Could not read backfill progress.'))
            pollTimerRef.current = setTimeout(() => {
                void pollRun(runId, gen)
            }, POLL_MS * 2)
        }
    }, [])

    const handleUpload = async (file: File) => {
        stopPolling()
        const gen = pollGenRef.current
        setUploading(true)
        setRunError('')
        setRun(null)
        try {
            const formData = new FormData()
            formData.append('file', file)
            const response = await apiUpload('signature-backfill-start', formData)
            if (!response.data?.success || !response.data.runId) {
                Report.failure('Error', response.data?.message || 'Could not start the CSV run.', 'Okay')
                setUploading(false)
                return
            }
            setRun({
                id: response.data.runId,
                status: 'running',
                filename: file.name,
                total: Number(response.data.total) || 0,
                processed: 0,
                failed: 0,
            })
            void pollRun(response.data.runId, gen)
        } catch (error) {
            setUploading(false)
            Report.failure('Error', errorMessage(error, 'Could not start the CSV run.'), 'Okay')
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const reasons = parseReasons(detail?.geometry_reasons)
    const src = imageSrc(detail?.signature_data)
    const runProgress =
        run && run.total > 0 ? Math.min(100, Math.round((Number(run.processed) / Number(run.total)) * 100)) : 0

    return (
        <div className={styles.page}>
            <section className={styles.uploadCard}>
                <div>
                    <h3>CSV backfill</h3>
                    <p>
                        Upload a CSV with <code>case_ref</code> and <code>signature_data</code>
                    </p>
                </div>
                <div className={styles.uploadActions}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className={styles.hiddenFileInput}
                        onChange={(event) => {
                            const file = event.target.files?.[0]
                            if (file) void handleUpload(file)
                        }}
                    />
                    <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? 'Uploading…' : 'Upload CSV'}
                    </button>
                </div>
                {run && (
                    <div className={styles.runStatus}>
                        <div className={styles.runMeta}>
                            <span>
                                Run #{run.id}
                                {run.filename ? ` · ${run.filename}` : ''}
                            </span>
                            <strong className={styles[`status_${run.status}`] || undefined}>{run.status}</strong>
                        </div>
                        <div className={styles.progressTrack}>
                            <div className={styles.progressFill} style={{ width: `${runProgress}%` }} />
                        </div>
                        <span>
                            {run.processed} / {run.total} processed
                            {run.failed ? ` · ${run.failed} failed` : ''}
                        </span>
                    </div>
                )}
                {runError ? <p className={styles.errorText}>{runError}</p> : null}
            </section>

            <div className={styles.split}>
                <section className={styles.listPane}>
                    <div className={styles.listHeader}>
                        <h3>Evaluations</h3>
                        <span>
                            {loaded ? `${total} result${total === 1 ? '' : 's'}` : 'Loading…'}
                            {total > 0 ? ` · page ${page} of ${pageCount}` : ''}
                        </span>
                    </div>

                    <div className={styles.filters}>
                        <label>
                            Final decision
                            <select
                                value={filters.final_decision}
                                onChange={(event) => patchFilters({ final_decision: event.target.value })}
                            >
                                <option value="">Any</option>
                                <option value="accept">accept</option>
                                <option value="reject">reject</option>
                                <option value="unsure">unsure</option>
                            </select>
                        </label>
                        <label>
                            Decided by
                            <select
                                value={filters.layer}
                                onChange={(event) => patchFilters({ layer: event.target.value })}
                            >
                                <option value="">Any</option>
                                <option value="geometry">geometry</option>
                                <option value="vlm">vlm</option>
                                <option value="human">human</option>
                            </select>
                        </label>
                        <label>
                            Source
                            <input
                                value={filters.source}
                                onChange={(event) => patchFilters({ source: event.target.value })}
                                placeholder="csvUpload"
                            />
                        </label>
                        <label>
                            Case ref
                            <input
                                value={filters.case_ref}
                                onChange={(event) => patchFilters({ case_ref: event.target.value })}
                                placeholder="Exact case ref"
                            />
                        </label>
                        <div className={styles.dateRangeField}>
                            <span>Date range</span>
                            <DatePicker
                                selectsRange
                                startDate={fromIsoDate(filters.dateFrom)}
                                endDate={fromIsoDate(filters.dateTo)}
                                onChange={([start, end]) =>
                                    patchFilters({
                                        dateFrom: start ? toIsoDate(start) : '',
                                        dateTo: end ? toIsoDate(end) : '',
                                    })
                                }
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Any date"
                                locale={enGB}
                                isClearable
                                portalId="root"
                            />
                        </div>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Case</th>
                                    <th>Source</th>
                                    <th>Final</th>
                                    <th>Geometry</th>
                                    <th>VLM</th>
                                    <th>Human</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className={styles.empty}>
                                            {loaded ? 'No evaluations match these filters.' : 'Loading…'}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className={`${styles.clickable} ${selectedId === row.id ? styles.selected : ''}`}
                                            onClick={() => void loadDetail(row.id)}
                                        >
                                            <td>
                                                <strong>{row.case_ref}</strong>
                                            </td>
                                            <td>{row.source}</td>
                                            <td>{row.final_decision || '—'}</td>
                                            <td>{row.geometry_decision || '—'}</td>
                                            <td>{row.vlm_decision || '—'}</td>
                                            <td>{row.human_decision || '—'}</td>
                                            <td className={styles.created}>{formatDateTime(row.created_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.pager}>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            disabled={page <= 1}
                            onClick={() => goToPage(Math.max(1, page - 1))}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            disabled={page >= pageCount}
                            onClick={() => goToPage(page + 1)}
                        >
                            Next
                        </button>
                    </div>
                </section>

                <section className={styles.detailPane}>
                    {!selectedId ? (
                        <p className={styles.placeholder}>Select a row to inspect the image and record a label.</p>
                    ) : detailLoading ? (
                        <p className={styles.placeholder}>Loading signature…</p>
                    ) : !detail ? (
                        <p className={styles.placeholder}>That evaluation could not be loaded.</p>
                    ) : (
                        <>
                            <div className={styles.detailHeader}>
                                <div>
                                    <h3>{detail.case_ref}</h3>
                                    <span>{detail.source}</span>
                                </div>
                            </div>
                            <div className={styles.imageFrame}>
                                {src ? (
                                    <img
                                        className={styles.signatureImage}
                                        src={src}
                                        alt={`Signature for ${detail.case_ref}`}
                                    />
                                ) : (
                                    <p className={styles.noImage}>No image on this evaluation.</p>
                                )}
                                <div className={styles.labelActions}>
                                    <button
                                        type="button"
                                        className={styles.acceptButton}
                                        disabled={labelling}
                                        title="Accept this signature"
                                        aria-label="Accept this signature"
                                        onClick={() => void recordLabel('accept')}
                                    >
                                        <FaCheck />
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.rejectButton}
                                        disabled={labelling}
                                        title="Reject this signature"
                                        aria-label="Reject this signature"
                                        onClick={() => void recordLabel('reject')}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            </div>
                            <dl className={styles.meta}>
                                <div>
                                    <dt>Final</dt>
                                    <dd>{detail.final_decision || '—'}</dd>
                                </div>
                                <div>
                                    <dt>Human</dt>
                                    <dd>{detail.human_decision || 'not labelled'}</dd>
                                </div>
                                <div>
                                    <dt>Geometry</dt>
                                    <dd>{detail.geometry_decision || '—'}</dd>
                                </div>
                                <div>
                                    <dt>VLM</dt>
                                    <dd>{detail.vlm_decision || '—'}</dd>
                                </div>
                            </dl>
                            <div className={styles.block}>
                                <h4>Geometry reasons</h4>
                                {reasons.length === 0 ? (
                                    <p>None recorded.</p>
                                ) : (
                                    <ul>
                                        {reasons.map((reason) => (
                                            <li key={reason}>{reason}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className={styles.block}>
                                <h4>VLM justification</h4>
                                <p>{detail.vlm_justification || 'None recorded.'}</p>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    )
}
